import { GlobalStore, UserStore } from '@roc/feature-app-core';
import {
  GENERIC_ERROR_MESSAGE,
  REFINANCE
} from '@roc/feature-utils';
import { action, computed, flow, makeObservable, observable } from 'mobx';
import { QuoteService } from '../services/quoteService';
import { LoanSubType, QuoteStep, rentalPortfolioQuoteSteps } from '../utils/constants';
import { BorrowersStore } from './common/borrowersStore';
import { EntityStore } from './fixFlip/entityStore';
import { FinalDetailsStore } from './fixFlip/finalDetailsStore';
import { RentalPortfolioLoanEconomicsStore } from './rentalPortfolio/rentalPortfolioLoanEconomicsStore';
import { RentalPortfolioInformationFormStore } from './rentalPortfolio/rentalPortfolioInformationFormStore';
import { RentalPortfolioPropertiesStore } from './rentalPortfolio/rentalPorfolioPropertiesStore';

const steps = rentalPortfolioQuoteSteps;

export class RentalPortfolioStore {
  globalStore: GlobalStore;
  userStore: UserStore;
  quoteService: QuoteService;

  capitalProviderName: string;
  savedQuote: any;
  savedLoan: any;

  entityStore: EntityStore;
  borrowersStore: BorrowersStore;
  portfolioInformationFormStore: RentalPortfolioInformationFormStore;
  propertiesStore: RentalPortfolioPropertiesStore;
  finalDetailsStore: FinalDetailsStore;
  loanEconomicsStore: RentalPortfolioLoanEconomicsStore;

  loanSubtype: LoanSubType;

  step: QuoteStep;
  lastCompletedStep: QuoteStep;

  quoteId: number;
  loanId: number;

  quoteNotFound: boolean;

  constructor(globalStore: GlobalStore, userStore: UserStore) {
    this.globalStore = globalStore;
    this.userStore = userStore;
    this.quoteService = new QuoteService();
    this.loanSubtype = LoanSubType.RENTAL_PORTFOLIO;
    this.borrowersStore = new BorrowersStore(
      this.globalStore,
      this.loanSubtype
    );
    this.entityStore = new EntityStore(
      globalStore,
      this.borrowersStore,
      this.loanSubtype
    );
    this.portfolioInformationFormStore = new RentalPortfolioInformationFormStore(globalStore);
    this.propertiesStore = new RentalPortfolioPropertiesStore(globalStore, this.loanSubtype);
    this.loanEconomicsStore = new RentalPortfolioLoanEconomicsStore(
      this.globalStore,
      this
    );
    this.finalDetailsStore = new FinalDetailsStore(globalStore, userStore);

    makeObservable(this, {
      savedQuote: observable,
      savedLoan: observable,
      capitalProviderName: observable,

      loanSubtype: observable,
      step: observable,
      quoteId: observable,
      reset: action,
      goToStep: flow,
      validateCurrentStep: action,
      saveQuote: flow,
      loadQuote: flow,
      loadLoan: flow,
      getQuoteData: action,
      quoteNotFound: observable,
      submitLoan: flow,
    });
  }

  reset() {
    this.savedQuote = null;
    this.savedLoan = null;
    this.capitalProviderName = null;

    this.step = QuoteStep.ENTITY_INFORMATION;
    this.lastCompletedStep = null;
    this.quoteId = null;
    this.loanId = null;
    this.borrowersStore.reset();
    this.portfolioInformationFormStore.reset();
    this.propertiesStore.reset();
    this.loanEconomicsStore.reset();
    this.entityStore.reset();
    this.quoteNotFound = false;
  }

  *goToStep(step: QuoteStep) {
    if (step === QuoteStep.SUMMARY) {
      this.borrowersStore.turnOffBorrowersEditMode();
    }

    if (steps.indexOf(step) < steps.indexOf(this.step)) {
      this.step = step;
    } else if (this.validateCurrentStep()) {
      if (this.canGoToStep(step)) {
        this.updateLastCompletedStep();
        this.step = step;
        yield this.saveQuote();
      } else {
        this.globalStore.notificationStore.showInfoNotification({
          message: 'Please fill the previous steps',
        });
      }
    }
  }

  private canGoToStep(step: QuoteStep) {
    return (
      steps.indexOf(step) <= steps.indexOf(this.lastCompletedStep) + 1 ||
      steps.indexOf(step) === steps.indexOf(this.step) + 1
    );
  }

  private updateLastCompletedStep() {
    if (steps.indexOf(this.lastCompletedStep) < steps.indexOf(this.step)) {
      this.lastCompletedStep = this.step;
    }
  }

  validateCurrentStep() {
    switch (this.step) {
      case QuoteStep.ENTITY_INFORMATION:
        return this.entityStore.validateEntity();
      case QuoteStep.BORROWER_INFORMATION:
        return this.borrowersStore.validateAllBorrowers();
      case QuoteStep.PORTFOLIO_INFORMATION:
        return this.portfolioInformationFormStore.validateFields();
      case QuoteStep.LOAN_PRICER:
        return this.loanEconomicsStore.validateLoanEconomicsStep();
      case QuoteStep.LOAN_ECONOMICS:
        return this.loanEconomicsStore.validateLoanEconomicsStep();
      case QuoteStep.LEVERAGE:
        return this.loanEconomicsStore.validateLeverageStep();
      default:
        return true;
    }
  }

  *saveQuote() {
    try {
      const isLoan = !!this.savedQuote?.loanNumber;
      const response = isLoan
        ? yield this.quoteService.saveLoan(this.getQuoteData())
        : yield this.quoteService.saveQuote(this.getQuoteData());
      this.savedQuote = response.data.data;
      this.quoteId = response.data.data.quoteId;
    } catch (e) {
      console.log(e);
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE,
      });
    }
  }

  *loadQuote(quoteNumber) {
    try {
      this.quoteNotFound = false;
      const response = yield this.quoteService.getQuoteByQuoteNumber(quoteNumber);
      const quoteData = response.data.data;
      const isSubmittedQuote = !!quoteData.loanId;
      if (!quoteData?.id || isSubmittedQuote) {
        throw new Error("Don't have access to the quote");
      }
      this.loadQuoteData(quoteData);
    } catch (e) {
      console.log(e);
      this.quoteNotFound = true;
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE,
      });
    }
  }

  *loadLoan(loanNumber) {
    try {
      this.quoteNotFound = false;
      const response = yield this.quoteService.getLoanByNumber(loanNumber);
      const quoteData = response.data.data;
      quoteData.uiState.quoteStep = QuoteStep.ENTITY_INFORMATION;
      quoteData.uiState.selectEntityOption = null;
      if (!quoteData?.id) {
        throw new Error("Don't have access to the loan");
      }
      this.loadQuoteData(quoteData);
    } catch (e) {
      console.log(e);
      this.quoteNotFound = true;
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE,
      });
    }
  }

  private loadQuoteData(quoteData) {
    this.savedQuote = quoteData;
    this.capitalProviderName = quoteData.capitalProviderName;
    this.quoteId = quoteData.quoteId;
    this.loanId = quoteData.loanId;
    this.entityStore.loadEntityData(quoteData);
    this.borrowersStore.loadBorrowers(quoteData.borrowers);
    this.portfolioInformationFormStore.loadPortfolioInformation(quoteData);
    this.propertiesStore.loadProperties(quoteData);
    this.loanEconomicsStore.loadLoanEconomics(quoteData);
    this.finalDetailsStore.loadClosingData(quoteData);
    this.step = quoteData.uiState.quoteStep;
  }

  setCapitalProviderName(name) {
    this.capitalProviderName = name;
  }

  getQuoteData() {
    return {
      ...(this.savedQuote ?? {}),
      schemaVersion: 1,
      capitalProviderName: this.capitalProviderName,
      lenderId: this.globalStore.lenderInfo.lenderId,
      loanType: this.loanSubtype,
      broker: this.loanEconomicsStore.getBroker(),
      entity: this.entityStore.getEntity(),
      borrowers: this.borrowersStore.getBorrowers(),
      properties: this.propertiesStore.getProperties(),
      portfolioInformation: this.portfolioInformationFormStore.getLoanPortfolioInformation(),
      closingData: this.finalDetailsStore.getClosingData(),
      loanEconomics: this.loanEconomicsStore.getLoanEconomics(),
      calculatedData: this.loanEconomicsStore.getCalculatedData(),
      assignment: this.finalDetailsStore.getAssignment(this.savedQuote),
      uiState: {
        quoteStep: this.step,
        selectEntityOption: this.entityStore.selectEntityOption,
      },
    };
  }

  *submitLoan() {
    try {
      // Validate properties before submitting
      const propertiesValid = this.propertiesStore.validateAllProperties();
      if (!propertiesValid) {
        return;
      }

      // Validate final details form
      this.finalDetailsStore.finalDetailsFormStore.runFormValidation();
      if (!this.finalDetailsStore.finalDetailsFormStore.form.meta.isValid) {
        this.globalStore.notificationStore.showWarningNotification({
          message: 'Please complete all the required fields in Final Details.',
        });
        return;
      }

      const isLoan = !!this.savedQuote?.loanNumber;
      const quoteData = this.getQuoteData();
      if (!isLoan) {
        quoteData.quoteId = quoteData.id;
        delete quoteData.id;
        delete quoteData.version;
      }
      const response = yield this.quoteService.saveLoan(quoteData);
      this.savedLoan = response.data.data;
      this.step = QuoteStep.SUCCESS;
    } catch (e) {
      console.log(e);
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE,
      });
    }
  }

  prepopulateProperties() {
    const propertyTypeFormStores = this.portfolioInformationFormStore.propertyTypeFormStores;
    const propertyFormStores = this.propertiesStore.propertyFormStores;

    if (!propertyTypeFormStores) {
      return;
    }

    const expectedTypes = this.getExpectedPropertyTypesCounts(propertyTypeFormStores);
    const existingTypes = this.getExistingPropertyTypesCounts(propertyFormStores);
    const allPropertyTypes = new Set([...Object.keys(expectedTypes), ...Object.keys(existingTypes)]);

    const portfolioLoanPurpose = this.portfolioInformationFormStore.form.fields.loanPurpose?.value || '';
    this.syncLoanPurposeToProperties(portfolioLoanPurpose);

    for (const propertyType of allPropertyTypes) {
      const expected = expectedTypes[propertyType] || 0;
      const existing = existingTypes[propertyType] || 0;
      const difference = expected - existing;

      if (difference > 0) {
        this.addPropertiesOfType(propertyType, difference, propertyFormStores.length === 0);
      } else if (difference < 0) {
        this.removePropertiesOfType(propertyType, Math.abs(difference), propertyFormStores);
      }
    }
  }

  private getExpectedPropertyTypesCounts(propertyTypeFormStores: any[]): Record<string, number> {
    const propertyTypes = propertyTypeFormStores.map(store => store.getFlattenedValues());

    return propertyTypes.reduce((acc, curr) => {
      const type = curr.propertyType;
      const count = +curr.numberOfProperties;
      acc[type] = (acc[type] || 0) + count;
      return acc;
    }, {} as Record<string, number>);
  }

  private getExistingPropertyTypesCounts(propertyFormStores: any[]): Record<string, number> {
    return propertyFormStores.reduce((acc, propertyFormStore) => {
      const propertyType = propertyFormStore.form.fields.propertyType?.value;
      if (propertyType) {
        acc[propertyType] = (acc[propertyType] || 0) + 1;
      }
      return acc;
    }, {} as Record<string, number>);
  }

  private addPropertiesOfType(propertyType: string, count: number, isFirstPropertyOverall: boolean) {
    const portfolioLoanPurpose = this.portfolioInformationFormStore.form.fields.loanPurpose?.value || '';
    const rentalPortfolioPropertiesStore = this.propertiesStore as RentalPortfolioPropertiesStore;

    for (let i = 0; i < count; i++) {
      const isFirstProperty = isFirstPropertyOverall && i === 0;
      rentalPortfolioPropertiesStore.addNewPropertyWithPropertyType(
        propertyType,
        isFirstProperty,
        portfolioLoanPurpose
      );
    }
  }

  private removePropertiesOfType(propertyType: string, count: number, propertyFormStores: any[]) {
    const indicesToRemove: number[] = [];

    for (let i = propertyFormStores.length - 1; i >= 0 && indicesToRemove.length < count; i--) {
      const propertyFormStore = propertyFormStores[i];
      const currentPropertyType = propertyFormStore.form.fields.propertyType?.value;

      if (currentPropertyType === propertyType) {
        indicesToRemove.push(i);
      }
    }

    indicesToRemove.sort((a, b) => b - a);
    indicesToRemove.forEach(index => {
      propertyFormStores.splice(index, 1);
    });
  }

  private syncLoanPurposeToProperties(loanPurpose: string) {
    this.propertiesStore.propertyFormStores.forEach(propertyStore => {
      const currentLoanPurpose = propertyStore.form.fields.loanPurpose?.value;
      if (currentLoanPurpose !== loanPurpose) {
        propertyStore.onFieldChange('loanPurpose', loanPurpose);
      }
    });
  }
}